import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useOnboarding } from '../context/OnboardingContext';
import { useLanguage } from '../context/LanguageContext';
import { usePWA } from '../context/PWAContext';
import { useAEAE } from '../hooks/useAEAE';
import { 
  MessageSquare, 
  Monitor, 
  Sparkles,
  ChevronRight,
  ChevronLeft,
  X,
  Check,
  Image,
  Rocket,
  Lightbulb,
  Wand2
} from 'lucide-react';

// ─── Message variants per emotional tone (AEAE) ───
const MESSAGE_VARIANTS = {
  standard: {
    es: {
      welcome: {
        title: 'Bienvenido a DamyxIA',
        description: 'Tu espacio de trabajo inteligente. Aca pensas, creas y transformas.'
      },
      chat: {
        title: 'Tu conversacion',
        description: 'Aca hablas con DamyxIA. Explicas lo que queres y pedis cambios.'
      },
      quickActions: {
        title: 'Acciones Rapidas',
        description: 'El bombillo azul abre 8 accesos directos: Grupos y Proyectos, Servicios y Guias, Resolucion por Pasos, Adjuntar Archivo, Generar Imagen, Galeria, Crear Diagrama e Instalar App. Todo a un solo clic, sin escribir un prompt.'
      },
      genio: {
        title: 'El Genio de DamyxIA',
        description: 'Este es tu Genio: tu asistente IA siempre presente en la cabecera. Pedile que abra herramientas, genere imagenes, te guie paso a paso, sugiera ideas o explore tus proyectos. Es la forma mas rapida y natural de moverte por DamyxIA: hablale como a un copiloto y deja que el haga el trabajo pesado por vos.'
      },
      workspace: {
        title: 'Tu proyecto vivo',
        descriptionMobile: 'El Workspace muestra tu app en tiempo real. Cada cambio se aplica como un parche incremental, sin recargar. Desliza a "Trabajo" para verlo.',
        descriptionDesktop: 'El Workspace muestra tu app o interfaz en tiempo real. Cada cambio se aplica como un parche incremental, sin recargar.'
      },
      vision: {
        title: 'Trabajo visual avanzado',
        description: 'Aca analizas imagenes y comparas versiones con IA.'
      },
      final: {
        title: 'Listo para crear',
        description: 'Tu chat es la conversacion. Tu Workspace es tu proyecto. Empecemos.'
      }
    },
    en: {
      welcome: {
        title: 'Welcome to DamyxIA',
        description: 'Your intelligent workspace. Think, create and transform here.'
      },
      chat: {
        title: 'Your conversation',
        description: 'Here you talk with DamyxIA. Explain what you want and request changes.'
      },
      quickActions: {
        title: 'Quick Actions',
        description: 'The blue lightbulb opens 8 shortcuts: Groups & Projects, Services & Guides, Step Resolution, Attach File, Generate Image, Gallery, Create Diagram and Install App. All one-click, no prompt required.'
      },
      genio: {
        title: 'The DamyxIA Genie',
        description: 'This is your Genie: your AI assistant always available in the header. Ask it to open tools, generate images, walk you through steps, suggest ideas or explore your projects. It is the fastest and most natural way to navigate DamyxIA — talk to it like a copilot and let it handle the heavy lifting.'
      },
      workspace: {
        title: 'Your live project',
        descriptionMobile: 'The Workspace shows your app in real time. Every change is applied as an incremental patch, no reload. Swipe to "Work" to see it.',
        descriptionDesktop: 'The Workspace shows your app or interface in real time. Every change is applied as an incremental patch, no reload.'
      },
      vision: {
        title: 'Advanced visual work',
        description: 'Here you analyze images and compare versions with AI.'
      },
      final: {
        title: 'Ready to create',
        description: 'Your chat is the conversation. Your Workspace is your project. Let\'s begin.'
      }
    }
  },
  calm: {
    es: {
      welcome: {
        title: 'Bienvenido a DamyxIA',
        description: 'Sin prisa. Este es tu espacio para pensar, crear y transformar ideas.'
      },
      chat: {
        title: 'Tu conversacion',
        description: 'Tomate tu tiempo. Aca le contas a DamyxIA lo que necesitas.'
      },
      quickActions: {
        title: 'Acciones Rapidas',
        description: 'Sin escribir prompts: el bombillo azul tiene 8 accesos directos para abrir Proyectos, Servicios, Imagenes, Diagramas y mas. Tomate tu tiempo para explorarlos.'
      },
      genio: {
        title: 'El Genio de DamyxIA',
        description: 'A tu lado en la cabecera: tu Genio. Pedile cosas con tranquilidad y el te guia, abre herramientas, propone ideas o resume tu actividad. Tomate tu tiempo para conocerlo; esta siempre disponible cuando lo necesites.'
      },
      workspace: {
        title: 'Tu proyecto vivo',
        descriptionMobile: 'El Workspace muestra resultados en tiempo real. Los cambios se aplican suavemente. Desliza a "Trabajo" cuando quieras.',
        descriptionDesktop: 'El Workspace muestra resultados en tiempo real. Los cambios se aplican suavemente, sin interrupciones.'
      },
      vision: {
        title: 'Trabajo visual',
        description: 'Cuando lo necesites, podes analizar y comparar imagenes con calma.'
      },
      final: {
        title: 'Todo listo',
        description: 'Explora a tu ritmo. Tu proyecto esta seguro.'
      }
    },
    en: {
      welcome: {
        title: 'Welcome to DamyxIA',
        description: 'No rush. This is your space to think, create and transform ideas.'
      },
      chat: {
        title: 'Your conversation',
        description: 'Take your time. Tell DamyxIA what you need here.'
      },
      quickActions: {
        title: 'Quick Actions',
        description: 'No prompts needed: the blue lightbulb has 8 shortcuts for Projects, Services, Images, Diagrams and more. Take your time to explore them.'
      },
      genio: {
        title: 'The DamyxIA Genie',
        description: 'By your side in the header: your Genie. Ask it calmly and it guides you, opens tools, suggests ideas or sums up your activity. Take your time to get to know it — it is always available when you need it.'
      },
      workspace: {
        title: 'Your live project',
        descriptionMobile: 'The Workspace shows results in real time. Changes apply smoothly. Swipe to "Work" when ready.',
        descriptionDesktop: 'The Workspace shows results in real time. Changes apply smoothly, no interruptions.'
      },
      vision: {
        title: 'Visual work',
        description: 'When you need it, you can analyze and compare images calmly.'
      },
      final: {
        title: 'All set',
        description: 'Explore at your pace. Your project is safe.'
      }
    }
  },
  energetic: {
    es: {
      welcome: {
        title: 'Bienvenido a DamyxIA',
        description: 'Tu espacio de trabajo inteligente. Vamos a crear algo increible!'
      },
      chat: {
        title: 'Tu conversacion',
        description: 'Aca empieza todo! Pedi codigo, imagenes, lo que necesites!'
      },
      quickActions: {
        title: 'Acciones Rapidas',
        description: 'Mira el bombillo azul! 8 accesos directos para volar: Proyectos, Servicios, Imagenes, Diagramas e Instalar App. Todo a un clic!'
      },
      genio: {
        title: 'El Genio de DamyxIA',
        description: 'Tu Genio esta en la cabecera, listo para volar! Pedile abrir tools, generar imagenes, contarte ideas o guiarte paso a paso. Es como tener un copiloto IA al toque: hablale y dispara!'
      },
      workspace: {
        title: 'Tu proyecto vivo',
        descriptionMobile: 'El Workspace muestra tu app al instante. Parches incrementales, sin recargar! Desliza a "Trabajo"!',
        descriptionDesktop: 'El Workspace muestra tu app al instante. Cada cambio llega como parche incremental!'
      },
      vision: {
        title: 'Trabajo visual avanzado',
        description: 'Analiza, compara y prepara imagenes con IA al toque!'
      },
      final: {
        title: 'A crear!',
        description: 'Chat + Workspace + herramientas visuales. Vamos!'
      }
    },
    en: {
      welcome: {
        title: 'Welcome to DamyxIA',
        description: 'Your intelligent workspace. Let\'s create something incredible!'
      },
      chat: {
        title: 'Your conversation',
        description: 'It all starts here! Request code, images, anything you need!'
      },
      quickActions: {
        title: 'Quick Actions',
        description: 'Look at the blue lightbulb! 8 shortcuts to fly: Projects, Services, Images, Diagrams and Install App. All one-click!'
      },
      genio: {
        title: 'The DamyxIA Genie',
        description: 'Your Genie is in the header, ready to fly! Ask it to open tools, generate images, share ideas or walk you through steps. Like having an AI copilot one tap away — talk to it and go!'
      },
      workspace: {
        title: 'Your live project',
        descriptionMobile: 'The Workspace shows your app instantly. Incremental patches, no reload! Swipe to "Work"!',
        descriptionDesktop: 'The Workspace shows your app instantly. Every change arrives as an incremental patch!'
      },
      vision: {
        title: 'Advanced visual work',
        description: 'Analyze, compare and prepare images with AI instantly!'
      },
      final: {
        title: 'Let\'s create!',
        description: 'Chat + Workspace + visual tools. Let\'s go!'
      }
    }
  },
  empathetic: {
    es: {
      welcome: {
        title: 'Bienvenido a DamyxIA',
        description: 'Estoy aca para ayudarte. Este es tu espacio seguro para crear.'
      },
      chat: {
        title: 'Tu conversacion',
        description: 'Contame lo que necesitas. Estoy escuchando, sin apuros.'
      },
      quickActions: {
        title: 'Acciones Rapidas',
        description: 'Si te resulta mas comodo no escribir, el bombillo azul te abre 8 accesos directos: Proyectos, Servicios, Imagenes, Diagramas y mas. Usalo cuando quieras.'
      },
      genio: {
        title: 'El Genio de DamyxIA',
        description: 'En la cabecera tenes a tu Genio acompañandote. Contale lo que necesitas y el te guia, abre herramientas, sugiere ideas o te ayuda a recordar donde quedaste. Pensalo como un amigo que conoce DamyxIA por dentro: usalo cuando quieras, sin presion.'
      },
      workspace: {
        title: 'Tu proyecto vivo',
        descriptionMobile: 'Los resultados aparecen aca. Cada cambio se cuida con parches suaves. Ve cuando estes listo.',
        descriptionDesktop: 'Los resultados aparecen aca. Cada cambio se cuida con parches suaves.'
      },
      vision: {
        title: 'Trabajo visual',
        description: 'Si necesitas trabajar con imagenes, estas herramientas estan para vos.'
      },
      final: {
        title: 'Estoy aca para vos',
        description: 'Tu proyecto esta seguro. Navega como te sea comodo.'
      }
    },
    en: {
      welcome: {
        title: 'Welcome to DamyxIA',
        description: 'I\'m here to help. This is your safe space to create.'
      },
      chat: {
        title: 'Your conversation',
        description: 'Tell me what you need. I\'m listening, no rush.'
      },
      quickActions: {
        title: 'Quick Actions',
        description: 'If typing feels easier when guided, the blue lightbulb opens 8 shortcuts: Projects, Services, Images, Diagrams and more. Use it whenever you like.'
      },
      genio: {
        title: 'The DamyxIA Genie',
        description: 'Your Genie is in the header keeping you company. Tell it what you need and it guides you, opens tools, suggests ideas or helps you pick up where you left off. Think of it as a friend who knows DamyxIA inside out — use it whenever you want, no pressure.'
      },
      workspace: {
        title: 'Your live project',
        descriptionMobile: 'Results appear here. Every change is handled with smooth patches. Go when you\'re ready.',
        descriptionDesktop: 'Results appear here. Every change is handled with smooth patches.'
      },
      vision: {
        title: 'Visual work',
        description: 'If you need to work with images, these tools are here for you.'
      },
      final: {
        title: 'I\'m here for you',
        description: 'Your project is safe. Navigate as comfortable.'
      }
    }
  }
};

// ─── Tone prefixes/suffixes ───
const toneAdjustments = {
  calm: { es: { prefix: '', suffix: '' }, en: { prefix: '', suffix: '' } },
  energetic: { es: { prefix: '', suffix: '' }, en: { prefix: '', suffix: '' } },
  empathetic: { es: { prefix: '', suffix: '' }, en: { prefix: '', suffix: '' } },
  standard: { es: { prefix: '', suffix: '' }, en: { prefix: '', suffix: '' } }
};

// ─── Step icon map ───
const STEP_ICONS = {
  welcome: Sparkles,
  chat: MessageSquare,
  quickActions: Lightbulb,
  genio: Wand2,
  workspace: Monitor,
  vision: Image,
  final: Rocket
};

// ─── Build onboarding steps ───
const getOnboardingSteps = (language, platform, messageVariant = 'standard') => {
  const isMobile = platform === 'ios' || platform === 'android';
  const variant = MESSAGE_VARIANTS[messageVariant] || MESSAGE_VARIANTS.standard;
  const msgs = variant[language] || variant.es;

  return [
    {
      id: 'welcome',
      target: null,
      title: msgs.welcome.title,
      description: msgs.welcome.description,
      icon: STEP_ICONS.welcome,
      position: 'center',
      highlight: null
    },
    {
      id: 'chat',
      target: '[data-testid="mobile-nav-chat"], [data-panel-id="chat-panel"]',
      mobileTarget: '[data-testid="mobile-nav-chat"]',
      desktopTarget: '[data-panel-id="chat-panel"]',
      title: msgs.chat.title,
      description: msgs.chat.description,
      icon: STEP_ICONS.chat,
      position: isMobile ? 'top' : 'right',
      highlight: 'chat'
    },
    {
      id: 'quickActions',
      target: '[data-testid="idea-lightbulb-btn"]',
      title: msgs.quickActions.title,
      description: msgs.quickActions.description,
      icon: STEP_ICONS.quickActions,
      position: isMobile ? 'top' : 'bottom',
      highlight: 'quickActions'
    },
    {
      // 🪄 Paso "Genio" — el asistente IA del header (data-testid="genio-open").
      // Pedido explícito del usuario: añadir tras "Ver Incorporación" un paso
      // que muestre el botón del Genio y explique qué puede hacer.
      id: 'genio',
      target: '[data-testid="genio-open"]',
      title: msgs.genio.title,
      description: msgs.genio.description,
      icon: STEP_ICONS.genio,
      position: isMobile ? 'top' : 'bottom',
      highlight: 'genio'
    },
    {
      id: 'workspace',
      target: '[data-testid="mobile-nav-workspace"], [data-panel-id="dashboard-panel"]',
      mobileTarget: '[data-testid="mobile-nav-workspace"]',
      desktopTarget: '[data-panel-id="dashboard-panel"]',
      title: msgs.workspace.title,
      description: isMobile ? msgs.workspace.descriptionMobile : msgs.workspace.descriptionDesktop,
      icon: STEP_ICONS.workspace,
      position: isMobile ? 'top' : 'left',
      highlight: 'workspace'
    },
    {
      id: 'vision',
      target: '[data-testid="header-gallery-btn"]',
      title: msgs.vision.title,
      description: msgs.vision.description,
      icon: STEP_ICONS.vision,
      position: isMobile ? 'center' : 'bottom-left',
      highlight: 'vision'
    },
    {
      id: 'final',
      target: null,
      title: msgs.final.title,
      description: msgs.final.description,
      icon: STEP_ICONS.final,
      position: 'center',
      highlight: null
    }
  ];
};

// ─── Spotlight component ───
const Spotlight = ({ targetSelector, animationSpeed }) => {
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!targetSelector) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const element = document.querySelector(targetSelector);
      if (element) {
        const rect = element.getBoundingClientRect();
        setPosition({
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16
        });
      } else {
        setPosition(null);
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [targetSelector]);

  if (!position) return null;

  const transitionDuration = animationSpeed === 'slow' ? '500ms' : animationSpeed === 'fast' ? '150ms' : '300ms';

  return (
    <div
      className="fixed pointer-events-none z-[99996] ease-out"
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
        height: position.height,
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.75)',
        borderRadius: '12px',
        border: '2px solid rgba(99, 102, 241, 0.8)',
        transition: `all ${transitionDuration} ease-out`
      }}
    >
      <div 
        className="absolute inset-0 rounded-xl border-2 border-indigo-400 opacity-50"
        style={{
          animation: `ping ${animationSpeed === 'slow' ? '2s' : animationSpeed === 'fast' ? '0.75s' : '1s'} cubic-bezier(0, 0, 0.2, 1) infinite`
        }}
      />
    </div>
  );
};

// ─── Tooltip component ───
const Tooltip = ({ 
  step, position, onNext, onPrev, onSkip, 
  currentIndex, totalSteps, messageVariant, 
  animationSpeed, language, isMobile 
}) => {
  const Icon = step.icon;
  const tone = toneAdjustments[messageVariant] || toneAdjustments.standard;
  const toneText = tone[language] || tone.es;
  const isLastStep = currentIndex === totalSteps - 1;
  const isFirstStep = currentIndex === 0;
  const transitionClass = animationSpeed === 'slow' ? 'duration-500' : animationSpeed === 'fast' ? 'duration-150' : 'duration-300';

  const getTooltipStyle = () => {
    const baseStyle = { maxWidth: isMobile ? '85vw' : '320px', width: isMobile ? '85vw' : '320px' };

    if (isMobile || position === 'center') {
      return { ...baseStyle, position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }

    switch (position) {
      case 'top': return { ...baseStyle, bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '16px' };
      case 'bottom': return { ...baseStyle, top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '16px' };
      case 'left': return { ...baseStyle, right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: '16px' };
      case 'right': return { ...baseStyle, left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: '16px' };
      case 'bottom-left': return { ...baseStyle, top: '100%', right: '0', marginTop: '16px' };
      default: return { ...baseStyle, position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
  };

  const getArrowClass = () => {
    if (isMobile) return 'hidden';
    switch (position) {
      case 'top': return 'bottom-[-8px] left-1/2 -translate-x-1/2 border-t-zinc-800 border-l-transparent border-r-transparent border-b-transparent';
      case 'bottom': return 'top-[-8px] left-1/2 -translate-x-1/2 border-b-zinc-800 border-l-transparent border-r-transparent border-t-transparent';
      case 'left': return 'right-[-8px] top-1/2 -translate-y-1/2 border-l-zinc-800 border-t-transparent border-b-transparent border-r-transparent';
      case 'right': return 'left-[-8px] top-1/2 -translate-y-1/2 border-r-zinc-800 border-t-transparent border-b-transparent border-l-transparent';
      case 'bottom-left': return 'top-[-8px] right-8 border-b-zinc-800 border-l-transparent border-r-transparent border-t-transparent';
      default: return 'hidden';
    }
  };

  return (
    <div 
      className={`absolute z-[99997] animate-in fade-in zoom-in-95 ${transitionClass}`}
      style={getTooltipStyle()}
    >
      {position !== 'center' && (
        <div className={`absolute w-0 h-0 border-8 ${getArrowClass()}`} />
      )}
      
      <div className="bg-zinc-800 border border-zinc-700 rounded-xl shadow-2xl overflow-visible min-w-fit">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border-b border-zinc-700">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-white text-sm">{step.title}</h3>
            <p className="text-xs text-zinc-400">{currentIndex + 1} / {totalSteps}</p>
          </div>
          <button onClick={onSkip} className={`p-1.5 hover:bg-zinc-700 rounded-lg transition-colors ${transitionClass}`} data-testid="onboarding-skip-btn">
            <X className="w-4 h-4 text-zinc-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          <p className="text-sm text-zinc-300 leading-relaxed">
            {toneText.prefix}{step.description}{toneText.suffix}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 pb-4 bg-zinc-900/50 border-t border-zinc-700 rounded-b-xl">
          <div className="flex gap-1.5">
            {Array.from({ length: totalSteps }).map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-all ${transitionClass} ${
                  idx === currentIndex ? 'bg-indigo-500 w-4' : idx < currentIndex ? 'bg-indigo-500/50' : 'bg-zinc-600'
                }`}
              />
            ))}
          </div>

          <div className="flex gap-2">
            {!isFirstStep && (
              <button onClick={onPrev} className={`flex items-center gap-1 px-3 py-1.5 text-sm text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors ${transitionClass}`} data-testid="onboarding-prev-btn">
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <button onClick={onNext} className={`flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors ${transitionClass}`} data-testid="onboarding-next-btn">
              {isLastStep ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>{language === 'es' ? 'Empezar' : 'Start'}</span>
                </>
              ) : (
                <>
                  <span>{language === 'es' ? 'Siguiente' : 'Next'}</span>
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Helper: tooltip position near target ───
const getTooltipPosition = (targetSelector, position) => {
  const element = document.querySelector(targetSelector);
  if (!element) {
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }
  const rect = element.getBoundingClientRect();
  const padding = 16;
  const tooltipWidth = 320;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let top, left;

  switch (position) {
    case 'top':
      top = rect.top - padding;
      left = rect.left + rect.width / 2;
      break;
    case 'bottom':
      top = rect.bottom + padding;
      left = rect.left + rect.width / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2;
      left = rect.left - padding;
      break;
    case 'right':
      top = rect.top + rect.height / 2;
      left = rect.right + padding;
      break;
    case 'bottom-left':
      top = rect.bottom + padding;
      left = rect.right - tooltipWidth;
      break;
    default:
      return {};
  }

  // Clamp to viewport: ensure tooltip doesn't overflow right edge
  if (left + tooltipWidth / 2 > viewportWidth - 16) {
    left = viewportWidth - tooltipWidth / 2 - 24;
  }
  if (left - tooltipWidth / 2 < 16) {
    left = tooltipWidth / 2 + 24;
  }
  // Clamp vertical
  if (top < 16) top = 16;
  if (top > viewportHeight - 100) top = viewportHeight - 100;

  return { top: `${top}px`, left: `${left}px` };
};

// ─── Main OnboardingTour component ───
const OnboardingTour = () => {
  const { 
    showOnboarding, currentStep, setTone: setOnboardingTone,
    nextStep, prevStep, skipOnboarding, completeOnboarding 
  } = useOnboarding();
  const { language } = useLanguage();
  const { isPWAInstalled, platform, canInstall } = usePWA();

  const { quickAnalyze, resetStepTimer } = useAEAE();

  const [currentVariant, setCurrentVariant] = useState('standard');
  const [currentAnimSpeed, setCurrentAnimSpeed] = useState('normal');

  const isMobile = platform === 'ios' || platform === 'android' || window.innerWidth < 768;

  useEffect(() => {
    const analyzeForStep = async () => {
      const stepContexts = ['welcome', 'chat', 'quickActions', 'genio', 'workspace', 'vision', 'final'];
      const result = await quickAnalyze(stepContexts[currentStep] || 'general');
      
      if (result) {
        setCurrentVariant(result.messageVariant || 'standard');
        setCurrentAnimSpeed(result.animationSpeed || 'normal');
        
        if (result.uiTone === 'calm' || result.uiTone === 'empathetic') {
          setOnboardingTone('calm');
        } else if (result.uiTone === 'energetic') {
          setOnboardingTone('energetic');
        } else {
          setOnboardingTone('neutral');
        }
      }
    };

    if (showOnboarding) {
      resetStepTimer();
      analyzeForStep();
    }
  }, [currentStep, showOnboarding, quickAnalyze, resetStepTimer, setOnboardingTone]);

  const steps = useMemo(() => {
    return getOnboardingSteps(language, platform, currentVariant);
  }, [language, platform, currentVariant]);

  const currentStepData = steps[currentStep];

  const targetSelector = useMemo(() => {
    if (!currentStepData?.target) return null;
    if (isMobile && currentStepData.mobileTarget) return currentStepData.mobileTarget;
    if (!isMobile && currentStepData.desktopTarget) return currentStepData.desktopTarget;
    return currentStepData.target;
  }, [currentStepData, isMobile]);

  const handleNext = useCallback(() => { nextStep(steps.length); }, [nextStep, steps.length]);
  const handlePrev = useCallback(() => { prevStep(); }, [prevStep]);
  const handleSkip = useCallback(() => { skipOnboarding(); }, [skipOnboarding]);

  if (!showOnboarding || !currentStepData) return null;

  return createPortal(
    <div className="fixed inset-0 z-[99995]" data-testid="onboarding-tour">
      <div 
        className={`absolute inset-0 bg-black/60 transition-opacity ${
          currentAnimSpeed === 'slow' ? 'duration-500' : currentAnimSpeed === 'fast' ? 'duration-150' : 'duration-300'
        }`}
        onClick={handleSkip}
      />

      <Spotlight targetSelector={targetSelector} animationSpeed={currentAnimSpeed} />

      <div 
        className={`${currentStepData.position === 'center' || isMobile ? '' : 'absolute'}`}
        style={
          currentStepData.position !== 'center' && targetSelector && !isMobile
            ? getTooltipPosition(targetSelector, currentStepData.position)
            : {}
        }
      >
        <Tooltip
          step={currentStepData}
          position={currentStepData.position}
          onNext={handleNext}
          onPrev={handlePrev}
          onSkip={handleSkip}
          currentIndex={currentStep}
          totalSteps={steps.length}
          messageVariant={currentVariant}
          animationSpeed={currentAnimSpeed}
          language={language}
          isMobile={isMobile}
        />
      </div>
    </div>,
    document.body
  );
};

export default OnboardingTour;
